import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { AttentionMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';

import { useConfig } from '../../../../defaultConfig';
import { useTransferAccountsContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { isLoanAccount } from '../../../../services/accountFilters';
import { getLoanArrearValue } from '../../../LoanList/loanUtils';
import { AmountInput, FromAccountSelect } from '../../../NettbankCreateTransactions/components';
import { MessageInput } from '../../../fields';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import DesktopDropdownPlaceholder from '../../DesktopDropdownPlaceholder';
import { DownpaymentActionType } from '../form/actions';
import { isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';
import { MAX_DOWNPAYMENT_LENGTH } from './consts';

const DownpaymentDesktopFields: React.FC<FieldProps> = ({
    form,
    loanAccount,
    locked,
    freeWithdrawals,
    allAccounts,
    validFromAccounts,
    invalidAccounts,
    accountState,
    selectLoanAccount,
    isFixedLoan,
}) => {
    const { featureToggles, paymentPageLink } = useConfig();
    const { state, errors, handleChange, curryChange, mutation } = form;
    const translated = useTranslation();

    const { targetFromAccount, targetToAccount } = useTransferAccountsContext();
    const [placeholderHeight, setPlaceholderHeight] = useState(0);

    const showLock = locked || mutation.isPending;

    const toAccountError = useMemo(() => {
        if (isFixedLoan && !errors.toAccount) {
            return {
                className: 'transaction-form__to-account-error',
                message: translated.downpayment.cannotDoDownpayment,
            };
        }
        return errors.toAccount
            ? { className: 'transaction-form__to-account-error', message: errors.toAccount }
            : undefined;
    }, [isFixedLoan, errors.toAccount, translated.downpayment.cannotDoDownpayment]);

    const loanArrears = featureToggles.enableLoanArrears ? getLoanArrearValue(loanAccount?.balance) : undefined;

    return (
        <>
            <FromAccountSelect
                className={classNames('transaction-form__from-account-selector open', {
                    error: errors.accountId || errors.toAccount || isFixedLoan,
                    'has-withdrawal': freeWithdrawals.show,
                })}
                id="transfer-from-account"
                label={translated.from}
                items={validFromAccounts ?? []}
                invalidItems={invalidAccounts}
                onSelect={curryChange(DownpaymentActionType.FROM_ACCOUNT_SELECTED)}
                placeholder={translated.chooseAccount}
                locked={showLock}
                isLoading={accountState.isLoading}
                selected={state.fromAccount}
                selectedAccount={targetFromAccount}
                error={
                    errors.accountId
                        ? { className: 'transaction-form__from-account-error', message: errors.accountId }
                        : undefined
                }
                isPaymentModalOpen
                bottomLabel={
                    freeWithdrawals.show ? (
                        <AccountFreeWithdrawalLabel
                            className="transaction-form__from-account-withdrawal"
                            freeWithdrawals={freeWithdrawals.data}
                        />
                    ) : undefined
                }
                setPlaceholderHeight={setPlaceholderHeight}
            />
            <FromAccountSelect
                className={classNames('transaction-form__to-account-selector open', {
                    error: errors.accountId || errors.toAccount || isFixedLoan,
                    'has-withdrawal': freeWithdrawals.show,
                })}
                id="transfer-to-account"
                label={translated.to}
                items={allAccounts?.filter(isLoanAccount) ?? []}
                onSelect={selectLoanAccount}
                placeholder={translated.chooseAccount}
                locked={showLock}
                isLoading={accountState.isLoading}
                selected={state.toAccount ?? loanAccount?.accountNumber}
                selectedAccount={targetToAccount ?? loanAccount}
                isPaymentModalOpen
                setPlaceholderHeight={setPlaceholderHeight}
                showNegativeAmount
                error={toAccountError}
            />
            <AmountInput
                className={classNames('transaction-form__amount-selector', { error: errors.amount })}
                value={state.amount}
                label={translated.payAmount}
                error={errors.amount}
                onChange={(value) => handleChange({ type: DownpaymentActionType.AMOUNT, payload: value })}
                isNoCoverage={isNoCoverage(state, targetFromAccount)}
                locked={showLock}
                currency={targetFromAccount?.currency}
            />
            <MessageInput
                className={classNames('transaction-form__message-selector', { error: errors.note })}
                noteValue={state.note}
                error={errors.note}
                onChange={(value) => handleChange({ type: DownpaymentActionType.NOTE, payload: value })}
                locked={showLock}
                maxLength={MAX_DOWNPAYMENT_LENGTH}
                disableKid
            />
            <DesktopDropdownPlaceholder height={placeholderHeight} />

            <AttentionMessage
                className="transaction-form__info-message"
                type="info"
                icon={IconTypes.INFO_DYN}
                text={translated.loans.infoTextDownPayment}
            />
            {loanArrears && (
                <AttentionMessage
                    className="transaction-form__info-message2"
                    type="attention"
                    icon={IconTypes.INFO_DYN}
                    text={translated.loans.arrearsWarning.description}
                    clickableItem={
                        <Link
                            linkType={LinkType.BUTTON_SMALL_NEGATIVE}
                            href={`${paymentPageLink?.url}?receiverAccountNumber=${loanAccount?.accountNumber.value}&amount=${loanArrears}`}
                        >
                            {translated.loans.arrearsWarning.buttonText}
                        </Link>
                    }
                />
            )}
        </>
    );
};

export default DownpaymentDesktopFields;
